import { Flex } from '@chakra-ui/react'
import { sortBy, values } from 'ramda'
import React, { useCallback } from 'react'

import { useTranslations } from '~shared-components/hooks/useTranslations'
import { IconTruck } from '~shared-components/icons/brand/IconTruck'
import { CustomerLocation } from '~shared-components/transforms'
import { SupportedCountry } from '~shared-components/utils/enums'

import { TruncatedText } from '../Misc/TruncatedText'
import { StandardText } from '../Typography/Typography'
import {
  DropdownButtonDisplayProps,
  DropdownOption,
  DropdownOptionDisplayProps,
  StandardDropdown,
  StandardDropdownProps,
} from './StandardDropdown'

const translateCountryCodeIntoCountry = (code: string) =>
  (Object.entries(SupportedCountry).find(([, value]) => value === code) ?? [
    code,
  ])[0]

export const toLocationDropdownOptions = (
  locations: CustomerLocation[] | undefined,
) =>
  sortBy<CustomerLocation>(item => item.name, locations || []).map(
    location => ({
      description: location.name,
      location,
      value: location.id,
    }),
  )

export interface LocationDropdownOption extends DropdownOption {
  location: CustomerLocation
}
interface LocationDropdownProps
  extends Omit<
    StandardDropdownProps<LocationDropdownOption>,
    'onOptionSelect' | 'children'
  > {
  onLocationChange: (location: CustomerLocation) => void
  withLabel?: boolean
}
type ExtraProps = Pick<LocationDropdownProps, 'withLabel'>
type LocationButtonDisplayProps = DropdownButtonDisplayProps<LocationDropdownOption>
type LocationButtonDisplayExtraProps = LocationButtonDisplayProps & ExtraProps
type LocationOptionDisplayProps = DropdownOptionDisplayProps<LocationDropdownOption>

const LocationButtonDisplay: React.FC<LocationButtonDisplayExtraProps> = ({
  disabled,
  maxWidth,
  options,
  value,
  withLabel,
}) => {
  const t = useTranslations()
  const displayColor = disabled ? 'neutral.10' : 'neutral.90'
  const selectedOption = options?.find(o => o.value === value)
  return (
    <Flex alignItems="center" height="36px" overflow="hidden" width="100%">
      {withLabel && <IconTruck boxSize="20px" color={displayColor} mr={4} />}
      <TruncatedText as="span" color={displayColor} maxWidth={maxWidth}>
        {selectedOption?.description || t('label.any')}
      </TruncatedText>
    </Flex>
  )
}

const LocationOptionDisplay: React.FC<LocationOptionDisplayProps> = ({
  option: {
    location: { name, streetLines = {}, zip, city, country },
    disabled,
  },
  isSelected,
}) => {
  const displayColor = disabled ? 'neutral.10' : 'inherit'

  return (
    <Flex
      className={isSelected ? 'display-option-selected' : ''}
      color={displayColor}
      direction="column"
      fontSize="body"
      whiteSpace="pre-line"
      width="100%"
    >
      <StandardText color="inherit" fontWeight="bold">
        {name}
      </StandardText>
      {values(streetLines)
        .filter(Boolean)
        .map(line => (
          <StandardText key={line} color="inherit">
            {line}
          </StandardText>
        ))}
      {(zip || city) && (
        <StandardText color="inherit">
          {[zip, city].filter(Boolean).join(', ')}
        </StandardText>
      )}
      {country && (
        <StandardText color="inherit">
          {translateCountryCodeIntoCountry(country)}
        </StandardText>
      )}
    </Flex>
  )
}

export const LocationDropdown: React.FC<LocationDropdownProps> = ({
  onLocationChange,
  withLabel,
  ...props
}) => {
  const LocationButtonDisplayAdapter = useCallback(
    (props: LocationButtonDisplayProps) => (
      <LocationButtonDisplay withLabel={withLabel} {...props} />
    ),
    [withLabel],
  )

  const handleOptionSelect = (
    _value: string,
    option?: LocationDropdownOption,
  ) => onLocationChange(option!.location)

  return (
    <StandardDropdown
      displayButton={LocationButtonDisplayAdapter}
      displayOption={LocationOptionDisplay}
      onOptionSelect={handleOptionSelect}
      {...props}
    />
  )
}
